import Bugsnag, { type Breadcrumb, type Client, type Event } from '@bugsnag/js';

/**
 * Browser notifier keys are public, write-only project identifiers. This key
 * cannot be used to read data from BugSnag and must be present in the bundle.
 */
export const BUGSNAG_API_KEY = 'a189c32c271167936a91a9cb063b7ae4';

export type BrowserErrorMetadata = Readonly<Record<string, Readonly<Record<string, unknown>>>>;

const REDACTED_KEYS: Array<string | RegExp> = [
  /api[-_]?key/i,
  /authorization/i,
  /cookie/i,
  /password/i,
  /secret/i,
  /token/i,
];

const ENABLED_BREADCRUMB_TYPES = ['error', 'manual', 'state'] as const;
const UNSAFE_SDK_METADATA_SECTIONS = [
  'unhandledrejection handler',
  'unhandledRejection handler',
  'uncaughtException handler',
  'window onerror',
  'error cause',
] as const;
const DEDUPLICATION_WINDOW_MS = 1_000;
export const BEFORE_UNLOAD_DELIVERY_TIMEOUT_MS = 2_000;
const URL_CANDIDATE = /(?:https?:\/\/|\/\/|\/)[^\s"'<>)}\]]+/gi;

let bugsnagClient: Client | null = null;
const lastReportedErrorObjects = new WeakMap<object, number>();
let lastPrimitiveError: { value: unknown; reportedAt: number } | null = null;

type ResettableHistory = History & {
  __r3BugsnagHistoryWrapped?: boolean;
  __r3BugsnagResetEventCount?: () => void;
};

const optionalEnvValue = (value: string | undefined): string | undefined => {
  const normalized = value?.trim();
  return normalized || undefined;
};

const releaseStage = (): string =>
  optionalEnvValue(import.meta.env.VITE_BUGSNAG_RELEASE_STAGE as string | undefined) ??
  import.meta.env.MODE;

const telemetryDisabled = (): boolean =>
  import.meta.env.MODE === 'test' || releaseStage().toLocaleLowerCase() === 'test';

const normalizedMetadataKey = (key: string): string =>
  key.toLocaleLowerCase().replace(/[^a-z]/g, '');

const isReferrerKey = (key: string): boolean => {
  const normalized = normalizedMetadataKey(key);
  return normalized.includes('referrer') || normalized.includes('referer');
};

const isUrlKey = (key: string): boolean => {
  const normalized = normalizedMetadataKey(key);
  return (
    normalized === 'from' ||
    normalized === 'to' ||
    normalized.includes('url') ||
    normalized.includes('uri') ||
    normalized.includes('href') ||
    normalized.includes('location') ||
    normalized.includes('page') ||
    normalized.includes('path') ||
    normalized.includes('redirect') ||
    normalized.includes('route')
  );
};

/** Strip private query parameters and fragments while preserving the URL path. */
export const sanitizeTelemetryUrl = (value: string): string => {
  const queryIndex = value.indexOf('?');
  const fragmentIndex = value.indexOf('#');
  const indexes = [queryIndex, fragmentIndex].filter((index) => index >= 0);
  const withoutPageState = indexes.length > 0 ? value.slice(0, Math.min(...indexes)) : value;
  let withoutCredentials = withoutPageState;

  if (/^(?:https?:)?\/\//i.test(withoutPageState)) {
    const protocolRelative = withoutPageState.startsWith('//');
    try {
      const parsed = new URL(protocolRelative ? `https:${withoutPageState}` : withoutPageState);
      parsed.username = '';
      parsed.password = '';
      withoutCredentials = protocolRelative
        ? parsed.toString().replace(/^https:/i, '')
        : parsed.toString();
    } catch {
      withoutCredentials = withoutPageState.replace(
        /^((?:https?:)?\/\/)[^/@\s]+@/i,
        '$1[REDACTED]@',
      );
    }
  }

  return withoutCredentials
    .replace(/(\/auth\/invitations\/)[^/]+/gi, '$1[REDACTED]')
    .replace(
      /(\/auth\/(?:email-verification|password-reset|reset-password|verify-email)\/(?:confirm\/)?)[^/]+/gi,
      '$1[REDACTED]',
    );
};

const sanitizeTelemetryText = (value: string): string =>
  value
    .replace(URL_CANDIDATE, (candidate) => sanitizeTelemetryUrl(candidate))
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[REDACTED_EMAIL]')
    .replace(
      /\b(authorization|cookie|password|passwd|secret|api[-_ ]?key|(?:access|refresh|id)[-_ ]?token|token)\b\s*(?:[:=]\s*|\s+)(?:(?:Bearer\s+)?(?:"[^"]*"|'[^']*'|[^\s,;]+))/gi,
      '$1=[REDACTED]',
    )
    .replace(/\bBearer\s+[A-Za-z0-9._~+/=-]+/gi, 'Bearer [REDACTED]')
    .replace(/\b(?:AKIA|ASIA)[A-Z0-9]{16}\b/g, '[REDACTED_CREDENTIAL]')
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, '[REDACTED_TOKEN]');

const sanitizeTelemetryValue = (value: unknown, key: string, seen: WeakSet<object>): unknown => {
  if (typeof value === 'string') {
    return isUrlKey(key) ? sanitizeTelemetryUrl(value) : sanitizeTelemetryText(value);
  }
  if (value instanceof URL) return sanitizeTelemetryUrl(value.toString());
  if (Array.isArray(value)) {
    return value.map((entry) => sanitizeTelemetryValue(entry, key, seen));
  }
  if (typeof value !== 'object' || value === null) return value;

  const prototype = Object.getPrototypeOf(value) as object | null;
  if (prototype !== Object.prototype && prototype !== null) return value;
  if (seen.has(value)) return '[Circular]';
  seen.add(value);

  const sanitized: Record<string, unknown> = {};
  for (const [childKey, childValue] of Object.entries(value)) {
    if (isReferrerKey(childKey)) continue;
    sanitized[childKey] = sanitizeTelemetryValue(childValue, childKey, seen);
  }
  return sanitized;
};

/** Sanitize app-owned metadata before it is handed to BugSnag. */
export const sanitizeTelemetryMetadata = (
  metadata: Readonly<Record<string, unknown>>,
): Record<string, unknown> =>
  sanitizeTelemetryValue(metadata, 'metadata', new WeakSet<object>()) as Record<string, unknown>;

const sanitizeBreadcrumb = (breadcrumb: Breadcrumb): void => {
  breadcrumb.message = sanitizeTelemetryText(breadcrumb.message);
  breadcrumb.metadata = sanitizeTelemetryMetadata(breadcrumb.metadata);
};

const isErrorLike = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== 'object' || value === null) return false;
  const tag = Object.prototype.toString.call(value);
  return (
    value instanceof Error ||
    tag === '[object Error]' ||
    tag === '[object Exception]' ||
    tag === '[object DOMException]'
  );
};

const replaceUnsafeOriginalError = (event: Event): boolean => {
  const safeError = new Error('An unhandled error was rejected without a stack');
  safeError.name = 'Error';
  safeError.stack = '';

  try {
    const mutableEvent = event as unknown as { originalError: unknown };
    mutableEvent.originalError = safeError;
    return mutableEvent.originalError === safeError;
  } catch {
    return false;
  }
};

const sanitizeOriginalErrorFields = (event: Event): boolean => {
  const originalError = event.originalError;
  if (!event.unhandled || !isErrorLike(originalError)) return true;
  try {
    if (originalError.stack) return true;
  } catch {
    return replaceUnsafeOriginalError(event);
  }
  if (typeof originalError !== 'object' || originalError === null) return true;

  const fields = originalError;
  for (const key of ['name', 'message', 'code']) {
    const value = fields[key];
    if (typeof value !== 'string') continue;
    const sanitized = sanitizeTelemetryText(value);
    if (sanitized === value) continue;

    try {
      fields[key] = sanitized;
      if (fields[key] !== sanitized) return replaceUnsafeOriginalError(event);
    } catch {
      return replaceUnsafeOriginalError(event);
    }
  }
  return true;
};

const sanitizeEvent = (event: Event): boolean => {
  const originalErrorIsSafe = sanitizeOriginalErrorFields(event);
  for (const section of UNSAFE_SDK_METADATA_SECTIONS) event.clearMetadata(section);

  const request = event.request as Record<string, unknown>;
  for (const [key, value] of Object.entries(request)) {
    if (isReferrerKey(key)) {
      delete request[key];
    } else if (isUrlKey(key) && typeof value === 'string') {
      request[key] = sanitizeTelemetryUrl(value);
    }
  }

  if (event.request.headers) {
    event.request.headers = sanitizeTelemetryMetadata(event.request.headers) as Record<
      string,
      string
    >;
  }
  if (event.response?.headers) {
    event.response.headers = sanitizeTelemetryMetadata(event.response.headers);
  }
  if (event.context) event.context = sanitizeTelemetryUrl(event.context);
  for (const breadcrumb of event.breadcrumbs) sanitizeBreadcrumb(breadcrumb);
  for (const error of event.errors) {
    error.errorClass = sanitizeTelemetryText(error.errorClass);
    error.errorMessage =
      event.originalError instanceof Error
        ? sanitizeTelemetryText(error.errorMessage)
        : 'A non-Error value was thrown';
    for (const frame of error.stacktrace) frame.file = sanitizeTelemetryUrl(frame.file);
  }
  for (const thread of event.threads) {
    for (const frame of thread.stacktrace) frame.file = sanitizeTelemetryUrl(frame.file);
  }
  return originalErrorIsSafe;
};

const installEventCountReset = (): void => {
  const history = window.history as ResettableHistory;
  history.__r3BugsnagResetEventCount = () => bugsnagClient?.resetEventCount?.();
  if (history.__r3BugsnagHistoryWrapped) return;

  const originalPushState = history.pushState.bind(history);
  const originalReplaceState = history.replaceState.bind(history);
  history.pushState = (...args: Parameters<History['pushState']>): void => {
    originalPushState(...args);
    history.__r3BugsnagResetEventCount?.();
  };
  history.replaceState = (...args: Parameters<History['replaceState']>): void => {
    originalReplaceState(...args);
    history.__r3BugsnagResetEventCount?.();
  };
  const reset = (): void => history.__r3BugsnagResetEventCount?.();
  window.addEventListener('popstate', reset, true);
  window.addEventListener('hashchange', reset, true);
  Object.defineProperty(history, '__r3BugsnagHistoryWrapped', {
    configurable: false,
    enumerable: false,
    value: true,
    writable: false,
  });
};

const errorObjectReference = (error: unknown): object | null => {
  if ((typeof error === 'object' && error !== null) || typeof error === 'function') {
    return error as object;
  }
  return null;
};

const normalizeError = (error: unknown): Error => {
  if (error instanceof Error) return error;

  if (typeof error === 'object' && error !== null) {
    const value = error as Record<string, unknown>;
    if (typeof value.status === 'number') {
      const statusText = typeof value.statusText === 'string' ? ` ${value.statusText}` : '';
      return new Error(`Route request failed: ${value.status}${statusText}`);
    }
  }

  return new Error('A non-Error value was thrown');
};

/**
 * Start the minimal error notifier before importing the React application.
 * Browser performance is started later, once the route objects are available.
 */
export const startBrowserErrorMonitoring = (): boolean => {
  if (bugsnagClient) return true;
  if (telemetryDisabled()) return false;

  const appVersion = optionalEnvValue(
    import.meta.env.VITE_BUGSNAG_APP_VERSION as string | undefined,
  );

  try {
    bugsnagClient = Bugsnag.start({
      apiKey: BUGSNAG_API_KEY,
      ...(appVersion ? { appVersion } : {}),
      releaseStage: releaseStage(),
      appType: 'r3-front',
      autoDetectErrors: true,
      maxEvents: 100,
      enabledErrorTypes: {
        unhandledExceptions: true,
        unhandledRejections: true,
      },
      autoTrackSessions: true,
      collectUserIp: false,
      enabledBreadcrumbTypes: [...ENABLED_BREADCRUMB_TYPES],
      generateAnonymousId: false,
      trackInlineScripts: false,
      redactedKeys: REDACTED_KEYS,
      onBreadcrumb: (breadcrumb) => {
        sanitizeBreadcrumb(breadcrumb);
        return true;
      },
      onError: sanitizeEvent,
    });
    installEventCountReset();
    return true;
  } catch {
    // Observability must never prevent the application from starting.
    return false;
  }
};

const notifyBrowserError = (
  error: unknown,
  metadata: BrowserErrorMetadata,
  onDelivery?: () => void,
): boolean => {
  if (!bugsnagClient) return false;

  const reportedAt = Date.now();
  const reference = errorObjectReference(error);
  const previousObjectReport = reference ? lastReportedErrorObjects.get(reference) : undefined;
  if (
    previousObjectReport !== undefined &&
    reportedAt - previousObjectReport < DEDUPLICATION_WINDOW_MS
  ) {
    return false;
  }
  if (reference) lastReportedErrorObjects.set(reference, reportedAt);

  if (
    !reference &&
    lastPrimitiveError &&
    Object.is(lastPrimitiveError.value, error) &&
    reportedAt - lastPrimitiveError.reportedAt < DEDUPLICATION_WINDOW_MS
  ) {
    return false;
  }
  if (!reference) lastPrimitiveError = { value: error, reportedAt };

  try {
    bugsnagClient.notify(
      normalizeError(error),
      (event) => {
        for (const [section, values] of Object.entries(metadata)) {
          event.addMetadata(section, sanitizeTelemetryMetadata(values));
        }
      },
      onDelivery ? () => onDelivery() : undefined,
    );
    return true;
  } catch {
    if (reference) lastReportedErrorObjects.delete(reference);
    if (!reference) lastPrimitiveError = null;
    return false;
  }
};

/** Report errors that application or router boundaries intentionally handle. */
export const captureBrowserError = (error: unknown, metadata: BrowserErrorMetadata = {}): void => {
  notifyBrowserError(error, metadata);
};

const isExpectedClientError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) return false;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' && status >= 400 && status < 500;
};

/** Report a handled action failure unless it is an expected client-side/API rejection. */
export const captureUnexpectedBrowserError = (
  error: unknown,
  metadata: BrowserErrorMetadata = {},
): void => {
  if (isExpectedClientError(error)) return;
  notifyBrowserError(error, metadata);
};

/**
 * Give an unexpected report a short, bounded delivery window before leaving
 * the page. This is reserved for paths that immediately perform an external
 * navigation, which can otherwise cancel BugSnag's asynchronous request.
 */
export const captureUnexpectedBrowserErrorBeforeUnload = async (
  error: unknown,
  metadata: BrowserErrorMetadata = {},
): Promise<void> => {
  if (isExpectedClientError(error)) return;

  await new Promise<void>((resolve) => {
    let settled = false;
    const complete = (): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      resolve();
    };
    const timeoutId = window.setTimeout(complete, BEFORE_UNLOAD_DELIVERY_TIMEOUT_MS);
    if (!notifyBrowserError(error, metadata, complete)) complete();
  });
};

/** Keep browser error reports correlated to an internal user ID only. */
export const setBrowserObservabilityUser = (userId: string | null): void => {
  if (!bugsnagClient) return;

  try {
    if (userId) {
      bugsnagClient.setUser(userId);
    } else {
      bugsnagClient.setUser();
    }
  } catch {
    // User context is enrichment only and must not affect auth state changes.
  }
};
